import {useEffect, useState} from "react";
import {Box, IconButton, Typography, useMediaQuery} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {keyframes} from "@emotion/react";
import {useTranslation} from "react-i18next";
import {useAssistant} from "./AssistantPanelContext";
import {
    ASSISTANT_FAB_MARGIN,
    ASSISTANT_FAB_MOBILE_MARGIN,
    ASSISTANT_FAB_MOBILE_SIZE,
    ASSISTANT_FAB_SIZE,
} from "./constants";

const CARD_GAP = 12;

const slideIn = keyframes`
    from { opacity: 0; transform: translateX(16px) scale(0.96); }
    to   { opacity: 1; transform: translateX(0) scale(1); }
`;

/**
 * "The team replied" card, parked above the launcher while the panel is closed.
 *
 * A coloured dot on the launcher was being missed outright, so the reply says itself in
 * words and holds until the customer acts on it — no auto-hide, which would put us back
 * where we started for anyone who stepped away. It deliberately quotes nothing of the
 * reply: the thread list carries no message text, and fetching it would buy little.
 *
 * Dismissing collapses back to the launcher dot without marking the reply read. Re-entering
 * the unread state re-arms the card; further replies inside the same unread run stay
 * collapsed, which is what dismissing asked for.
 */
const AssistantReplyCard = () => {
    const {t} = useTranslation();
    const assistant = useAssistant();
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const [dismissed, setDismissed] = useState(false);
    const status = assistant?.status;

    useEffect(() => {
        setDismissed(false);
    }, [status]);

    if (!assistant) return null;
    const {isOpen, open} = assistant;
    if (isOpen || status !== "unreadAnswer" || dismissed) return null;

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    open();
                }
            }}
            aria-label={t("The team replied")}
            className="hide-on-print"
            sx={{
                position: "fixed",
                bottom: {
                    xs: ASSISTANT_FAB_MOBILE_MARGIN + ASSISTANT_FAB_MOBILE_SIZE + CARD_GAP,
                    sm: ASSISTANT_FAB_MARGIN + ASSISTANT_FAB_SIZE + CARD_GAP,
                },
                right: {xs: ASSISTANT_FAB_MOBILE_MARGIN, sm: ASSISTANT_FAB_MARGIN},
                zIndex: 1250,
                display: "flex",
                alignItems: "center",
                gap: 1,
                pl: 1.25,
                pr: 0.5,
                py: 0.75,
                cursor: "pointer",
                borderRadius: "10px",
                bgcolor: "#fff",
                // a white card on a white dashboard needs the lift to register at all
                border: "1px solid rgba(0, 27, 68, 0.14)",
                boxShadow: "0 6px 20px rgba(15, 23, 42, 0.18)",
                animation: prefersReducedMotion ? "none" : `${slideIn} 260ms cubic-bezier(0.2, 0.9, 0.3, 1)`,
                transition: "box-shadow 150ms ease",
                "&:hover": {boxShadow: "0 8px 24px rgba(15, 23, 42, 0.24)"},
                "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.light",
                    outlineOffset: 2,
                },
            }}
        >
            <Box
                sx={{
                    width: 7,
                    height: 7,
                    flex: "none",
                    borderRadius: "50%",
                    bgcolor: "error.main",
                }}
            />
            <Typography sx={{fontSize: 12, fontWeight: 600, color: "primary.main", whiteSpace: "nowrap"}}>
                {t("The team replied")}
            </Typography>
            <IconButton
                size="small"
                aria-label={t("Dismiss")}
                onClick={event => {
                    event.stopPropagation();
                    setDismissed(true);
                }}
                sx={{p: 0.25, color: "text.disabled", "&:hover": {color: "text.secondary"}}}
            >
                <CloseIcon sx={{fontSize: 14}}/>
            </IconButton>
        </Box>
    );
};

export default AssistantReplyCard;
